import { useState, useEffect, useRef } from 'react';
import { Settings, X } from 'lucide-react';
import { logger } from '../../lib/terminalLogger';
import { corelCommunication } from '../../lib/corel/communication';
import { useNotifications } from '../feedback/NotificationProvider';
import { notifyToolsError } from '../../lib/toolsErrorNotification';
import { usePermissions } from '../../contexts/PermissionContext';
import { storage } from '../../lib/storage/storage';
import { Keys } from '../../lib/storage/keys';

// ═══════════════════════════════════════════════════════════════════════════
// Tipos e helpers CMYK
// ═══════════════════════════════════════════════════════════════════════════

interface CmykColor {
  c: number;
  m: number;
  y: number;
  k: number;
}

interface OneContornoColorPrefs {
  line: string;
}

const DEFAULT_OUTLINE_COLOR: CmykColor = { c: 0, m: 0, y: 0, k: 100 };

interface LocalizedVarnishColor {
  key: string;
  model: string;
  displayName: string;
  previewHex: string;
  count: number;
  c?: number | null;
  m?: number | null;
  y?: number | null;
  k?: number | null;
  r?: number | null;
  g?: number | null;
  b?: number | null;
  paletteIdentifier?: string | null;
  spotColorName?: string | null;
  tint?: number | null;
  sourceType?: string;
  selected?: boolean;
}

function hexToCmyk(hex: string): CmykColor {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const kVal = 1 - Math.max(r, g, b);
  if (kVal === 1) return { c: 0, m: 0, y: 0, k: 100 };
  return {
    c: Math.round(((1 - r - kVal) / (1 - kVal)) * 100),
    m: Math.round(((1 - g - kVal) / (1 - kVal)) * 100),
    y: Math.round(((1 - b - kVal) / (1 - kVal)) * 100),
    k: Math.round(kVal * 100),
  };
}

function cmykToHex(cmyk: CmykColor): string {
  const { c, m, y, k } = cmyk;
  const r = Math.round(255 * (1 - c / 100) * (1 - k / 100));
  const g = Math.round(255 * (1 - m / 100) * (1 - k / 100));
  const b = Math.round(255 * (1 - y / 100) * (1 - k / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

function isOneContornoColorPrefs(value: unknown): value is OneContornoColorPrefs {
  if (!value || typeof value !== 'object') return false;
  const prefs = value as Partial<OneContornoColorPrefs>;
  return isHexColor(prefs.line);
}

function summarizeLocalizedColor(color: LocalizedVarnishColor): string {
  if (color.model === 'CMYK') {
    return `CMYK ${color.c ?? 0},${color.m ?? 0},${color.y ?? 0},${color.k ?? 0}`;
  }
  if (color.model === 'CMY') {
    return `CMY ${color.c ?? 0},${color.m ?? 0},${color.y ?? 0}`;
  }
  if (color.model === 'RGB') {
    return `RGB ${color.r ?? 0},${color.g ?? 0},${color.b ?? 0}`;
  }
  if (color.model === 'Gray') {
    return `Gray ${color.k ?? 0}`;
  }
  if (color.model === 'Spot') {
    return color.paletteIdentifier
      ? `${color.paletteIdentifier}${color.tint != null ? `, ${color.tint}%` : ''}`
      : color.tint != null ? `${color.tint}%` : 'Spot';
  }
  if (color.model === 'Registration') {
    return 'Registration';
  }
  if (color.model === 'BlackAndWhite') {
    return color.displayName || 'Black/White';
  }
  return color.model;
}

// ═══════════════════════════════════════════════════════════════════════════
// Color Picker CMYK
// ═══════════════════════════════════════════════════════════════════════════

function CmykColorPicker({ label, cmyk, onChange, disabled, showCmykFields = true }: {
  label: string;
  cmyk: CmykColor;
  onChange: (cmyk: CmykColor) => void;
  disabled?: boolean;
  showCmykFields?: boolean;
}) {
  const hexValue = cmykToHex(cmyk);

  const handleHexChange = (hex: string) => {
    onChange(hexToCmyk(hex));
  };

  const handleCmykField = (field: keyof CmykColor, value: string) => {
    const num = parseInt(value);
    if (isNaN(num)) return;
    onChange({ ...cmyk, [field]: Math.max(0, Math.min(100, num)) });
  };

  return (
    <div className={disabled ? 'opacity-40 pointer-events-none' : ''}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={hexValue}
          onChange={(e) => handleHexChange(e.target.value)}
          className="w-7 h-7 rounded border border-navy-600 bg-navy-900 cursor-pointer p-0"
        />
        {showCmykFields && (
          <div className="grid grid-cols-4 gap-1 flex-1">
            {(['c', 'm', 'y', 'k'] as const).map((field) => (
              <input
                key={field}
                type="number"
                min={0}
                max={100}
                value={cmyk[field]}
                onChange={(e) => handleCmykField(field, e.target.value)}
                className="w-full bg-navy-900 border border-navy-600 rounded px-1 py-0.5 text-[10px] text-gray-300 text-center focus:border-blue-500 focus:outline-none"
                title={field.toUpperCase()}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Toggle
// ═══════════════════════════════════════════════════════════════════════════

function Toggle({ label, checked, onChange, disabled }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-center gap-2 cursor-pointer select-none ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
      <div
        className={`w-8 h-4 rounded-full transition-colors relative ${checked ? 'bg-blue-600' : 'bg-navy-600'}`}
        onClick={() => onChange(!checked)}
      >
        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
      <span className="text-xs text-gray-300">{label}</span>
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Radio Group
// ═══════════════════════════════════════════════════════════════════════════

function RadioGroup<T extends string>({ options, value, onChange }: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex-1 px-2 py-1.5 text-xs rounded transition-colors ${
            value === opt.value
              ? 'bg-blue-600 text-white'
              : 'bg-navy-800 text-gray-400 hover:bg-navy-700 hover:text-gray-300'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Checkbox com label
// ═══════════════════════════════════════════════════════════════════════════

function Checkbox({ label, checked, onChange, color = 'blue' }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  color?: 'orange' | 'purple' | 'blue';
}) {
  const colors = {
    orange: 'bg-orange-600 border-orange-500',
    purple: 'bg-purple-600 border-purple-500',
    blue: 'bg-blue-600 border-blue-500',
  };
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
          checked ? colors[color] : 'border-navy-500 bg-navy-900'
        }`}
        onClick={() => onChange(!checked)}
      >
        {checked && (
          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <span className="text-sm text-gray-200 font-medium">{label}</span>
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// OneContornoPage
// ═══════════════════════════════════════════════════════════════════════════

type Direction = 'externo' | 'interno';
type OperationMode = 'free' | 'finishing';
type SourceMode = 'asIs' | 'asOne' | 'withinGroups';
type ResultPlacement = 'above' | 'below';
type ResultFormat = 'objects' | 'group' | 'layer';

export function OneContornoPage() {
  const { notify } = useNotifications();
  const { role, userInfo } = usePermissions();
  const userId = (userInfo as any)?.id;
  const colorPrefsKey = Keys.prefs.onecontorno.colors(role, userId);
  const colorPrefsReadyRef = useRef(false);
  const [colorPrefsReady, setColorPrefsReady] = useState(false);
  const [operationMode, setOperationMode] = useState<OperationMode>('free');

  // Checkboxes
  const [recorteEnabled, setRecorteEnabled] = useState(false);
  const [vernizEnabled, setVernizEnabled] = useState(false);
  const [localizedEnabled, setLocalizedEnabled] = useState(false);

  // Recorte config
  const [recorteDirection, setRecorteDirection] = useState<Direction>('externo');
  const [recorteOffset, setRecorteOffset] = useState('3.0');

  // Verniz config
  const [vernizDirection, setVernizDirection] = useState<Direction>('externo');
  const [vernizOffset, setVernizOffset] = useState('0.0');

  // Contorno livre (quando nenhum checkbox marcado)
  const [contornoDirection, setContornoDirection] = useState<Direction>('externo');
  const [contornoOffset, setContornoOffset] = useState('3.0');
  const [roundCorners, setRoundCorners] = useState(true);
  const [autoPowerClip, setAutoPowerClip] = useState(false);

  // Outline (contorno livre)
  const [makeOutline, setMakeOutline] = useState(true);
  const [outlineWidth, setOutlineWidth] = useState('0.1');
  const [outlineColor, setOutlineColor] = useState<CmykColor>(DEFAULT_OUTLINE_COLOR);

  // Fill (contorno livre)
  const [makeFill, setMakeFill] = useState(false);
  const [useFillColor, setUseFillColor] = useState(false);
  const [matchColor, setMatchColor] = useState(true);
  const [fillColor, setFillColor] = useState<CmykColor>({ c: 0, m: 0, y: 0, k: 0 });

  // Avancado
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [traceEnabled, setTraceEnabled] = useState(true);
  const [sourceMode, setSourceMode] = useState<SourceMode>('asIs');
  const [resultPlacement, setResultPlacement] = useState<ResultPlacement>('above');
  const [resultFormat, setResultFormat] = useState<ResultFormat>('objects');
  const [name, setName] = useState('contorno');

  const [isSending, setIsSending] = useState(false);
  const [isAnalyzingLocalized, setIsAnalyzingLocalized] = useState(false);
  const [localizedColors, setLocalizedColors] = useState<LocalizedVarnishColor[]>([]);
  const [localizedSelectionSignature, setLocalizedSelectionSignature] = useState<string | null>(null);
  const [localizedBitmapCount, setLocalizedBitmapCount] = useState<number>(0);
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [progressMessage, setProgressMessage] = useState<string>('');

  const isPresetMode = operationMode === 'finishing';
  const selectedLocalizedColors = localizedColors.filter((color) => color.selected);
  const hasPresetOperation = recorteEnabled || vernizEnabled || localizedEnabled;
  const presetOperationCount = [recorteEnabled, vernizEnabled, localizedEnabled].filter(Boolean).length;
  const autoPowerClipUnsupported = autoPowerClip && (
    isPresetMode
      ? localizedEnabled || presetOperationCount !== 1
      : sourceMode === 'asOne' || resultFormat === 'group'
  );
  const canApply = !isSending && (
    isPresetMode
      ? hasPresetOperation
        && (!recorteEnabled || recorteOffset.trim() !== '')
        && (!vernizEnabled || vernizOffset.trim() !== '')
        && (!localizedEnabled || selectedLocalizedColors.length > 0)
      : contornoOffset.trim() !== ''
  ) && !autoPowerClipUnsupported;

  useEffect(() => {
    colorPrefsReadyRef.current = false;
    setColorPrefsReady(false);

    try {
      const saved = storage.get<OneContornoColorPrefs>(colorPrefsKey, {
        area: 'local',
        validate: isOneContornoColorPrefs,
      });

      setOutlineColor(saved ? hexToCmyk(saved.line) : DEFAULT_OUTLINE_COLOR);
    } catch {
      setOutlineColor(DEFAULT_OUTLINE_COLOR);
    } finally {
      colorPrefsReadyRef.current = true;
      setColorPrefsReady(true);
    }
  }, [colorPrefsKey]);

  useEffect(() => {
    if (!colorPrefsReady || !colorPrefsReadyRef.current) return;

    try {
      storage.set<OneContornoColorPrefs>(
        colorPrefsKey,
        { line: cmykToHex(outlineColor) },
        { area: 'local' }
      );
    } catch {
      // Preferencia visual: falha de storage nao deve bloquear a ferramenta.
    }
  }, [colorPrefsReady, colorPrefsKey, outlineColor]);

  const computeOffset = (offset: string, direction: Direction): number | null => {
    const parsed = parseFloat(offset.replace(',', '.'));
    if (isNaN(parsed)) return null;
    return direction === 'interno' ? -Math.abs(parsed) : Math.abs(parsed);
  };

  const handleCaptureSelectedColor = async () => {
    if (!localizedEnabled || isAnalyzingLocalized) return;

    setIsAnalyzingLocalized(true);

    try {
      const result = await new Promise<any>((resolve) => {
        corelCommunication.sendActionGuarded('contorno', {
          mode: 'captureSelectedColor',
        }, (resp: any) => {
          resolve(resp);
        });
      });

      if (result?.status !== 'ok') {
        notifyToolsError(notify, 'OneContorno', result?.message || 'Erro ao capturar cor.');
        return;
      }

      const newColors = Array.isArray(result?.result?.colors) ? result.result.colors as LocalizedVarnishColor[] : [];
      if (newColors.length === 0) {
        notifyToolsError(notify, 'OneContorno', 'Nenhuma cor capturada.');
        return;
      }

      const captured = newColors[0];

      setLocalizedColors((prev) => {
        const exists = prev.some((c) => c.key === captured.key);
        if (exists) {
          return prev;
        }
        return [...prev, { ...captured, selected: true }];
      });

      notify({
        type: 'success',
        title: 'Cor capturada',
        message: captured.displayName || 'Cor adicionada à lista.',
        sticky: false,
      });
    } catch (error: any) {
      logger.error('[OneContorno] Erro ao capturar cor:', error);
      notifyToolsError(notify, 'OneContorno', error?.message || 'Erro inesperado ao capturar cor.');
    } finally {
      setIsAnalyzingLocalized(false);
    }
  };

  const handleAnalyzeLocalizedColors = async () => {
    if (!localizedEnabled || isAnalyzingLocalized) return;

    setIsAnalyzingLocalized(true);
    setProgressPercent(0);
    setProgressMessage('Coletando shapes...');

    try {
      const result = await new Promise<any>((resolve) => {
        corelCommunication.sendActionGuarded('contorno', {
          mode: 'analyzeLocalizedColors',
          sourceMode,
        }, (resp: any) => {
          if (resp?.status === 'progress_update') {
            const pct = resp?.result?.percentComplete ?? resp?.percentComplete ?? 0;
            const msg = resp?.result?.message ?? resp?.message ?? '';
            const nextPct = Math.max(0, Math.min(100, pct));
            setProgressPercent((prev) => prev == null ? nextPct : Math.max(prev, nextPct));
            setProgressMessage(msg);
            return;
          }
          resolve(resp);
        });
      });

      if (result?.status !== 'ok') {
        notifyToolsError(notify, 'OneContorno', result?.message || 'Erro ao ler as cores da selecao.');
        return;
      }

      const colors = Array.isArray(result?.result?.colors) ? result.result.colors as LocalizedVarnishColor[] : [];
      const previousSelection = new Set(
        localizedColors.filter((color) => color.selected).map((color) => color.key),
      );

      setLocalizedColors(colors.map((color) => ({
        ...color,
        selected: previousSelection.has(color.key),
      })));
      setLocalizedSelectionSignature(result?.result?.selectionSignature ?? null);
      setLocalizedBitmapCount(result?.result?.bitmapShapes ?? 0);

      const bitmaps = result?.result?.bitmapShapes ?? 0;
      if (colors.length === 0 && bitmaps > 0) {
        notify({
          type: 'warning',
          title: 'Apenas bitmaps detectados',
          message: 'O verniz localizado funciona apenas com vetores. Selecione objetos vetoriais.',
          sticky: false,
        });
      } else if (colors.length > 0 && bitmaps > 0) {
        notify({
          type: 'info',
          title: 'Cores carregadas',
          message: `${colors.length} cor(es) vetorial(is). ${bitmaps} bitmap(s) serão ignorado(s).`,
          sticky: false,
        });
      } else {
        notify({
          type: 'success',
          title: 'Cores carregadas',
          message: result?.message || `${colors.length} cor(es) encontrada(s).`,
          sticky: false,
        });
      }
    } catch (error: any) {
      logger.error('[OneContorno] Erro ao ler cores:', error);
      notifyToolsError(notify, 'OneContorno', error?.message || 'Erro inesperado ao ler as cores.');
    } finally {
      setIsAnalyzingLocalized(false);
      setProgressPercent(null);
      setProgressMessage('');
    }
  };

  const handleApply = async () => {
    if (!canApply) return;

    setIsSending(true);
    setProgressPercent(3);
    setProgressMessage('Preparando envio...');

    try {
      let data: any;

      if (isPresetMode) {
        // Multi-pass mode: monta array de passes
        const passes: any[] = [];

        if (recorteEnabled) {
          const offset = computeOffset(recorteOffset, recorteDirection);
          if (offset === null) {
            notify({ type: 'error', title: 'Valor invalido', message: 'Distancia do recorte deve ser um numero.', sticky: false });
            setIsSending(false);
            return;
          }
          passes.push({ preset: 'recorte', offset });
        }

        if (vernizEnabled) {
          const offset = computeOffset(vernizOffset, vernizDirection);
          if (offset === null) {
            notify({ type: 'error', title: 'Valor invalido', message: 'Distancia do verniz deve ser um numero.', sticky: false });
            setIsSending(false);
            return;
          }
          passes.push({ preset: 'verniz', offset });
        }

        data = {
          passes,
          autoPowerClip,
          roundCorners,
          traceEnabled,
          sourceMode,
          resultPlacement,
          localizedVarnish: localizedEnabled ? {
            enabled: true,
            selectionSignature: localizedSelectionSignature,
            colors: selectedLocalizedColors.map((color) => ({
              key: color.key,
              model: color.model,
              displayName: color.displayName,
              previewHex: color.previewHex,
              count: color.count,
              c: color.c,
              m: color.m,
              y: color.y,
              k: color.k,
              r: color.r,
              g: color.g,
              b: color.b,
              paletteIdentifier: color.paletteIdentifier ?? null,
              spotColorName: color.spotColorName ?? null,
              tint: color.tint ?? null,
              sourceType: color.sourceType ?? null,
            })),
          } : undefined,
        };
      } else {
        // Single pass: contorno livre
        const offset = computeOffset(contornoOffset, contornoDirection);
        if (offset === null) {
          notify({ type: 'error', title: 'Valor invalido', message: 'Distancia deve ser um numero.', sticky: false });
          setIsSending(false);
          return;
        }

        const parsedWidth = parseFloat(outlineWidth.replace(',', '.'));
        if (makeOutline && (isNaN(parsedWidth) || parsedWidth <= 0)) {
          notify({ type: 'error', title: 'Valor invalido', message: 'Largura do outline deve ser positiva.', sticky: false });
          setIsSending(false);
          return;
        }

        data = {
          preset: 'contorno',
          offset,
          roundCorners,
          makeOutline,
          outlineWidth: parsedWidth,
          outlineColor,
          makeFill,
          useFillColor,
          matchColor,
          fillColor,
          autoPowerClip,
          traceEnabled,
          sourceMode,
          resultPlacement,
          resultFormat,
          name: name.trim() || 'contorno',
        };
      }

      logger.debug('[OneContorno] Enviando:', data);

      const result = await new Promise<any>((resolve) => {
        corelCommunication.sendActionGuarded('contorno', data, (resp: any) => {
          if (resp?.status === 'progress_update') {
            const pct = resp?.result?.percentComplete ?? resp?.percentComplete ?? 0;
            const msg = resp?.result?.message ?? resp?.message ?? '';
            const nextPct = Math.max(0, Math.min(100, pct));
            setProgressPercent((prev) => prev == null ? nextPct : Math.max(prev, nextPct));
            setProgressMessage(msg);
            return;
          }
          resolve(resp);
        });
      });

      logger.debug('[OneContorno] Resultado:', result);

      if (result?.status === 'ok') {
        notify({
          type: 'success',
          title: isPresetMode ? 'Processamento concluido' : 'Contorno aplicado',
          message: result?.message || 'Operacao concluida com sucesso.',
          sticky: false,
        });
      } else {
        notifyToolsError(notify, 'OneContorno', result?.message || 'Erro ao criar contorno.');
      }
    } catch (error: any) {
      logger.error('[OneContorno] Erro:', error);
      notifyToolsError(notify, 'OneContorno', error?.message || 'Erro inesperado.');
    } finally {
      setIsSending(false);
      setProgressPercent(null);
      setProgressMessage('');
    }
  };

  // Enter para aplicar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isSending) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Label do botao
  const activePresetLabels = isPresetMode
    ? [
        recorteEnabled ? 'Recorte' : null,
        vernizEnabled ? 'Verniz' : null,
        localizedEnabled ? 'Verniz Localizado' : null,
      ].filter(Boolean) as string[]
    : [];

  const buttonLabel = isSending
    ? 'Aplicando...'
    : activePresetLabels.length > 0
      ? `Aplicar ${activePresetLabels.join(' + ')}`
      : isPresetMode ? 'Aplicar Acabamentos' : 'Aplicar Contorno';

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3 pb-14 space-y-3">
        <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-2 space-y-2">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              className="inline-flex items-center gap-1.5 rounded bg-navy-700/60 px-2 py-1 text-xs text-gray-300 hover:bg-navy-700 hover:text-white transition-colors"
            >
              <Settings className="h-3.5 w-3.5" />
              Avançado
            </button>
          </div>
          <RadioGroup<OperationMode>
            options={[
              { value: 'free', label: 'Contorno livre' },
              { value: 'finishing', label: 'Acabamentos' },
            ]}
            value={operationMode}
            onChange={setOperationMode}
          />
        </section>

        {isPresetMode && (
          <>
        <div className="grid grid-cols-2 gap-2">
        {/* === Recorte === */}
        <section className={`border rounded-lg p-3 space-y-2 transition-colors ${
          recorteEnabled ? 'bg-orange-950/30 border-orange-700/60' : 'bg-navy-800/60 border-navy-700'
        }`}>
          <Checkbox
            label="Recorte"
            checked={recorteEnabled}
            onChange={setRecorteEnabled}
            color="orange"
          />

          {recorteEnabled && (
            <div className="space-y-2 pt-1">
              <RadioGroup<Direction>
                options={[
                  { value: 'externo', label: 'Externo' },
                  { value: 'interno', label: 'Interno' },
                ]}
                value={recorteDirection}
                onChange={setRecorteDirection}
              />
              <div>
                <label className="block text-xs text-gray-400 mb-1">Distância (mm)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={recorteOffset}
                  onChange={(e) => setRecorteOffset(e.target.value)}
                  placeholder="3.0"
                  className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-orange-500 focus:outline-none"
                />
              </div>
            </div>
          )}
        </section>

        {/* === Verniz === */}
        <section className={`border rounded-lg p-3 space-y-2 transition-colors ${
          vernizEnabled ? 'bg-purple-950/30 border-purple-700/60' : 'bg-navy-800/60 border-navy-700'
        }`}>
          <Checkbox
            label="Verniz"
            checked={vernizEnabled}
            onChange={setVernizEnabled}
            color="purple"
          />

          {vernizEnabled && (
            <div className="space-y-2 pt-1">
              <RadioGroup<Direction>
                options={[
                  { value: 'externo', label: 'Externo' },
                  { value: 'interno', label: 'Interno' },
                ]}
                value={vernizDirection}
                onChange={setVernizDirection}
              />
              <div>
                <label className="block text-xs text-gray-400 mb-1">Distância (mm)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={vernizOffset}
                  onChange={(e) => setVernizOffset(e.target.value)}
                  placeholder="0.0"
                  className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>
          )}
        </section>
        </div>

        {/* === Verniz localizado === */}
        <section className={`border rounded-lg p-3 space-y-2 transition-colors ${
          localizedEnabled ? 'bg-sky-950/30 border-sky-700/60' : 'bg-navy-800/60 border-navy-700'
        }`}>
          <Checkbox
            label="Verniz localizado"
            checked={localizedEnabled}
            onChange={setLocalizedEnabled}
            color="blue"
          />

          {localizedEnabled && (
            <div className="space-y-2 pt-1">
              {/* Botão principal: Ler cores da seleção */}
              <button
                type="button"
                onClick={handleAnalyzeLocalizedColors}
                disabled={isAnalyzingLocalized || isSending}
                className={`w-full rounded px-2 py-2 text-xs font-medium transition-colors ${
                  isAnalyzingLocalized || isSending
                    ? 'bg-navy-700 text-gray-500 cursor-not-allowed'
                    : 'bg-sky-600 hover:bg-sky-700 text-white'
                }`}
              >
                {isAnalyzingLocalized ? 'Analisando...' : 'Ler cores da selecao'}
              </button>
              {/* Linha 2: Todas / Limpar */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setLocalizedColors((colors) => colors.map((color) => ({ ...color, selected: true })))}
                  disabled={localizedColors.length === 0}
                  className={`flex-1 rounded px-2 py-2 text-xs font-medium transition-colors ${
                    localizedColors.length === 0
                      ? 'bg-navy-700 text-gray-500 cursor-not-allowed'
                      : 'bg-navy-700 hover:bg-navy-600 text-gray-200'
                  }`}
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => setLocalizedColors((colors) => colors.map((color) => ({ ...color, selected: false })))}
                  disabled={localizedColors.length === 0}
                  className={`flex-1 rounded px-2 py-2 text-xs font-medium transition-colors ${
                    localizedColors.length === 0
                      ? 'bg-navy-700 text-gray-500 cursor-not-allowed'
                      : 'bg-navy-700 hover:bg-navy-600 text-gray-200'
                  }`}
                >
                  Limpar
                </button>
              </div>

              {localizedColors.length > 0 ? (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {localizedColors.map((color) => (
                    <label
                      key={color.key}
                      className={`flex items-start gap-3 rounded border px-2 py-2 cursor-pointer transition-colors ${
                        color.selected
                          ? 'border-sky-500/60 bg-sky-950/30'
                          : 'border-navy-700 bg-navy-900/70 hover:bg-navy-900'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!color.selected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setLocalizedColors((colors) => colors.map((item) => (
                            item.key === color.key ? { ...item, selected: checked } : item
                          )));
                        }}
                        className="mt-0.5"
                      />
                      <div
                        className="w-5 h-5 rounded border border-white/10 flex-shrink-0"
                        style={{ backgroundColor: color.previewHex || '#0f172a' }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-gray-100 truncate">
                          {color.displayName}
                          {color.sourceType === 'bitmap' && (
                            <span className="ml-1.5 text-[9px] bg-amber-700/60 text-amber-200 px-1 py-0.5 rounded">
                              BITMAP
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {summarizeLocalizedColor(color)} {'\u2022'} {color.count} objeto(s)
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              ) : localizedBitmapCount > 0 ? (
                <div className="rounded border border-amber-600/40 bg-amber-950/20 px-3 py-3 text-xs text-amber-200">
                  <div className="font-medium mb-1">{localizedBitmapCount} bitmap(s) detectado(s)</div>
                  <div className="text-amber-300/70">
                    O verniz localizado funciona apenas com objetos vetoriais.
                    Bitmaps (PNGs, JPGs) na seleção serão ignorados.
                    Selecione objetos vetoriais para usar esta ferramenta.
                  </div>
                </div>
              ) : (
                <div className="rounded border border-dashed border-navy-700 px-3 py-3 text-xs text-gray-500">
                  Nenhuma cor carregada ainda.
                </div>
              )}
            </div>
          )}
        </section>
          </>
        )}

        {/* === Contorno livre === */}
        {!isPresetMode && (
          <>
            {/* Direcao + Distancia */}
            <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3 space-y-2">
              <div className="text-xs text-gray-400 font-medium mb-1">Direção</div>
              <RadioGroup<Direction>
                options={[
                  { value: 'externo', label: 'Externo' },
                  { value: 'interno', label: 'Interno' },
                ]}
                value={contornoDirection}
                onChange={setContornoDirection}
              />
              <div>
                <label className="block text-xs text-gray-400 mb-1">Distância (mm)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={contornoOffset}
                  onChange={(e) => setContornoOffset(e.target.value)}
                  placeholder="3.0"
                  className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <Toggle
                label="Cantos arredondados"
                checked={roundCorners}
                onChange={setRoundCorners}
              />
            </section>

            {/* Outline */}
            <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3 space-y-2">
              <Toggle
                label="Linha do contorno"
                checked={makeOutline}
                onChange={setMakeOutline}
              />
              {makeOutline && (
                <>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Largura (mm)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={outlineWidth}
                      onChange={(e) => setOutlineWidth(e.target.value)}
                      placeholder="0.1"
                      className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <CmykColorPicker
                    label="Cor da linha"
                    cmyk={outlineColor}
                    onChange={setOutlineColor}
                    showCmykFields={false}
                  />
                </>
              )}
            </section>

            {/* Preenchimento */}
            <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3 space-y-2">
              <Toggle
                label="Preenchimento"
                checked={makeFill}
                onChange={setMakeFill}
              />
              {makeFill && (
                <>
                  <Toggle
                    label="Copiar cor do objeto"
                    checked={matchColor}
                    onChange={setMatchColor}
                  />
                  <Toggle
                    label="Usar cor personalizada"
                    checked={useFillColor}
                    onChange={setUseFillColor}
                    disabled={matchColor}
                  />
                  {useFillColor && !matchColor && (
                    <CmykColorPicker
                      label="Cor do preenchimento"
                      cmyk={fillColor}
                      onChange={setFillColor}
                    />
                  )}
                </>
              )}
            </section>
          </>
        )}

        {/* === Cantos arredondados (modo preset) === */}
        {isPresetMode && (
          <div className="px-1">
            <Toggle
              label="Cantos arredondados"
              checked={roundCorners}
              onChange={setRoundCorners}
            />
          </div>
        )}

        <div className="px-1 space-y-1">
          <Toggle
            label="Auto PowerClip"
            checked={autoPowerClip}
            onChange={setAutoPowerClip}
          />
          {autoPowerClipUnsupported && (
            <p className="pl-10 text-[10px] text-amber-300/80">
              Use com um acabamento por vez, sem verniz localizado, origem unificada ou resultado agrupado.
            </p>
          )}
        </div>

        {/* === Barra de progresso === */}
        {progressPercent !== null && (
          <div className="space-y-1">
            <div className="w-full bg-navy-700 rounded h-2 overflow-hidden">
              <div
                className="bg-cyan-500 h-2 transition-all duration-300"
                style={{ width: `${Math.max(0, Math.min(100, progressPercent))}%` }}
              />
            </div>
            {progressMessage && (
              <p className="text-[10px] text-gray-400 text-center">{progressMessage}</p>
            )}
          </div>
        )}

        {/* === Aplicar === */}
        <button
          onClick={handleApply}
          disabled={!canApply}
          className={`w-full py-2 rounded text-sm font-medium transition-colors ${
            canApply
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-navy-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          {buttonLabel}
        </button>

        {showAdvanced && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
            <div className="w-full max-w-[360px] overflow-hidden rounded-lg border border-navy-700 bg-navy-800 shadow-xl">
              <div className="flex items-center justify-between border-b border-navy-700 px-3 py-2.5">
                <div className="flex items-center gap-2 text-sm font-medium text-white">
                  <Settings className="h-4 w-4 text-gray-400" />
                  Configurações avançadas
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(false)}
                  className="rounded p-1 text-gray-400 hover:bg-navy-700 hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

          <div className="max-h-[75vh] space-y-3 overflow-y-auto p-3">
            {/* Origem */}
            <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3 space-y-2">
              <div className="text-xs text-gray-400 font-medium">Origem</div>
              <RadioGroup<SourceMode>
                options={[
                  { value: 'asIs', label: 'Cada um' },
                  { value: 'asOne', label: 'Como um' },
                  { value: 'withinGroups', label: 'Dentro de grupos' },
                ]}
                value={sourceMode}
                onChange={setSourceMode}
              />
              <p className="text-[10px] text-gray-500">
                {sourceMode === 'asIs' && 'Processa cada objeto separadamente'}
                {sourceMode === 'asOne' && 'Solda todos os contornos em um unico shape'}
                {sourceMode === 'withinGroups' && 'Trata shapes dentro de grupos como separados'}
              </p>
            </section>

            {/* Resultado (somente contorno livre) */}
            {!isPresetMode && (
              <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3 space-y-2">
                <div className="text-xs text-gray-400 font-medium">Resultado</div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Posicao</label>
                  <RadioGroup<ResultPlacement>
                    options={[
                      { value: 'above', label: 'Acima' },
                      { value: 'below', label: 'Abaixo' },
                    ]}
                    value={resultPlacement}
                    onChange={setResultPlacement}
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Formato</label>
                  <RadioGroup<ResultFormat>
                    options={[
                      { value: 'objects', label: 'Objetos' },
                      { value: 'group', label: 'Grupo' },
                      { value: 'layer', label: 'Camada' },
                    ]}
                    value={resultFormat}
                    onChange={setResultFormat}
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Nome</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="contorno"
                    maxLength={16}
                    className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </section>
            )}

            {/* Opcoes */}
            <section className="bg-navy-800/60 border border-navy-700 rounded-lg p-3">
              <Toggle
                label="Auto-trace bitmaps"
                checked={traceEnabled}
                onChange={setTraceEnabled}
              />
            </section>
          </div>

              <div className="border-t border-navy-700 p-3">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(false)}
                  className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
